import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Calculator, Menu, X, ChevronDown } from 'lucide-react';
import { CATEGORIES } from '@/lib/tools';
import { CURRENCIES, useCurrency } from '@/lib/currency';

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/tools', label: 'Tools' },
  { to: '/categories', label: 'Categories' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const { currency, setCurrencyCode } = useCurrency();

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-100"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
              <Calculator className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="leading-none">
              <div className="text-lg font-bold tracking-tight text-slate-900">CalculateEasy</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Free online tools</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'text-emerald-600 bg-emerald-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                onBlur={() => setTimeout(() => setCatOpen(false), 150)}
                data-testid="nav-categories-dropdown"
                className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 flex items-center gap-1"
                aria-expanded={catOpen}
                aria-haspopup="menu"
              >
                Explore <ChevronDown className="w-4 h-4" />
              </button>
              {catOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-64 bg-white border border-slate-100 rounded-xl shadow-lg shadow-slate-200/50 p-2"
                >
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.id}
                      to={`/categories#${c.id}`}
                      role="menuitem"
                      data-testid={`nav-category-${c.id}`}
                      className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <label htmlFor="currency-select" className="sr-only">Currency</label>
            <select
              id="currency-select"
              data-testid="currency-selector"
              value={currency.code}
              onChange={(e) => setCurrencyCode(e.target.value)}
              className="hidden sm:block text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.code}</option>
              ))}
            </select>
            <Link
              to="/tools"
              data-testid="header-browse-tools-btn"
              className="hidden md:inline-flex items-center px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm hover:shadow-md"
            >
              Browse tools
            </Link>
            <button
              type="button"
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium ${
                    isActive ? 'text-emerald-600 bg-emerald-50' : 'text-slate-700 hover:bg-slate-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50">
              <label htmlFor="currency-select-mobile" className="text-sm font-medium text-slate-700">Currency</label>
              <select
                id="currency-select-mobile"
                data-testid="currency-selector-mobile"
                value={currency.code}
                onChange={(e) => setCurrencyCode(e.target.value)}
                className="text-sm bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                {CURRENCIES.map((c) => {
                  const label = `${c.code} — ${c.label}`;
                  return <option key={c.code} value={c.code}>{label}</option>;
                })}
              </select>
            </div>
            <Link
              to="/tools"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex justify-center items-center px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold"
            >
              Browse tools
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
